import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { Table, TableBody, TableSortLabel, Paper, Button, TableRow, TableHead, TableCell, Grid, Box } from "@mui/material";
import { Pagination, PageLoader, STableLoader, FilterComponent, jetStreamInstance, gaTelesisInstance } from "../../../shared_elements";
import { getAssetsApi, getAircraftTypeList, getEngineTypeList } from "../apiServices";
import { getAssetListApi } from "../../leases/apiServices";
import NoRecordFound from "../../../shared_elements/NoRecordFound";
import { globalGetService } from "../../../utils/globalApiServices";
import AssetsList from "../components/AssetsList";
import AddAssetBtn from "../components/AddAssetBtn";
import { assetTypeMap } from "../components/AssetsConstants";
import appConfig from "../../../config";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ButtonLoader } from "../../../shared_elements/components";
import { checkApiStatus } from "../../../utils";
import { enqueueSnackbar } from "notistack";
import { trackActivity } from "../../../utils/mixpanel";
import AssetTabs from "../components/AssetTabs";
import { useDispatch, useSelector } from "react-redux";
import { getLesseeApi, getOwnerListApi, getOwnerLessorApi } from "../../../shared_elements/actionCreators";
import VariableRentModal from "../components/VariableRentModal";
import DeploymentBannerWithOffset from "../../../shared_elements/components/DeploymentBannerWithOffSet";
import { ASSET_TYPE_OPTIONS } from "../../../constants";

const assetStatusOptions = [
  { label: 'On Ground', value: 3 },
  { label: 'On Lease', value: 1 },
  { label: 'Written Off / Sold / Part out', value: 4 },
  { label: 'Archive', value: 5 },
  { label: 'Off Lease', value: 2 },
];
const ownerShipTypes = [
  { label: 'Owned', value: '1' },
  { label: 'Managed', value: '2' },
  { label: 'Third Party', value: '3' }
]


const AssetsHd = ({ createSortHandler, sortInfo, toggleBulkOps, item, bulk, activeTab }) => {
  const showRegistration = activeTab === 0 || activeTab === 1 || (activeTab === 2 && gaTelesisInstance);

  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Assets`, page_type: page_type, })
  }
  const tableSorting = (key, name, disableSort = false) => (
    <TableSortLabel
      disabled={disableSort}
      hideSortIcon={disableSort}
      sx={{ whiteSpace: "nowrap" }}
      active={sortInfo.sort_by === key}
      direction={sortInfo.sort_by === key ? (sortInfo.sort || "asc").toLowerCase() : "asc"}
      onClick={() => { createSortHandler(key); handleItemClicked(`Table Sorting: ${name}`) }}
    >
      {name}
    </TableSortLabel>
  );

  return (
    <TableHead>
      <TableRow>
        <TableCell className="table-actions-header" sx={{ whiteSpace: "nowrap", minWidth: "60px" }}>Action(s)</TableCell>
        <TableCell  sx={{ minWidth: '150px' }}>{tableSorting("asset", "Asset")}</TableCell>
        
        <TableCell  sx={{ minWidth: '150px' }}>{tableSorting("lessor", "Lessor/Holdco",)}</TableCell>
        <TableCell  sx={{ minWidth: '150px' }}>{tableSorting("owner", "Owner/SPV")}</TableCell>
        <TableCell sx={{ minWidth: '150px' }}>{tableSorting("lessee", "Lessee")}</TableCell>
        {jetStreamInstance ? <TableCell sx={{ minWidth: '150px' }}>{tableSorting("next_use", "Next Use")}</TableCell> : null}
        {showRegistration ? <TableCell sx={{ minWidth: '100px' }}>{tableSorting("current_registration_number", "Registration")}</TableCell> : null}
        <TableCell  sx={{ minWidth: '150px' }}>{tableSorting("country", "Country")}</TableCell>
        <TableCell sx={{ minWidth: '150px' }}>{tableSorting("location", "Location")}</TableCell>
        <TableCell>{tableSorting("status", "Status")}</TableCell>
        <TableCell sx={{ minWidth: '100px' }}>{tableSorting("basic_rent", "Basic Rent", true)}</TableCell>
      </TableRow>
    </TableHead>
  );
};

const Assets = () => {

  const domain = appConfig.domain.subDomian
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const params = useParams();
  const { lessee, lessor, owner } = useSelector((state) => state.leases);
  const [isLoading, setLoading] = useState(false);
  const [skeletonLoader, setSkeletonLoader] = useState(false);
  const [assetsInfo, setiAssetsInfo] = useState({ list: [], pagination: {} });
  const [filter, setFilter] = useState({});
  const [assetType, setAssetType] = useState(0);
  const [sortInfo, setSortInfo] = useState({ sort: '', sort_by: '' });
  const [isExport, seIsExport] = useState(false);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [engineTypes, setEngineTypes] = useState([]);
  const [assetList, setAssetList] = useState([]);
  const [assetListLoading, setAssetListLoading] = useState(false);
  const assetCount = assetsInfo?.asset_count || {};
  const hasAircraft = (assetCount.aircraft || 0) > 0;
  const hasEngine = (assetCount.engine || 0) > 0;
  const hasAnyAssetCount = Object.values(assetCount).some(value => typeof value === "number" && value > 0);
  // Any applied filter (aircraft/engine type, asset dropdown, status, etc.) narrows the actual
  // result list — show that real count on the active tab instead of the unfiltered breakdown.
  const hasAppliedFilter = Object.keys(filter || {}).length > 0;
  const activeTabCount = hasAppliedFilter ? (assetsInfo?.pagination?.total ?? null) : null;
  const [variableRent, setVariableRent] = useState({ modal: false, data: null })
  const [drawerAssetType, setDrawerAssetType] = useState(undefined);
  const [drawerAircraftType, setDrawerAircraftType] = useState(undefined);
  const [drawerEngineType, setDrawerEngineType] = useState(undefined);

  useEffect(() => {
    setDrawerAssetType(undefined);
    setDrawerAircraftType(undefined);
    setDrawerEngineType(undefined);
  }, [filter.asset_type, filter.aircraft_type, filter.engine_type]);

  const AssetFilterMenu = useMemo(() => {
    const activeAssetType = (drawerAssetType !== undefined ? drawerAssetType : (filter.asset_type !== undefined ? filter.asset_type : assetType)) || 0;
    const activeAircraftType = drawerAircraftType === undefined ? filter.aircraft_type : drawerAircraftType;
    const activeEngineType = drawerEngineType === undefined ? filter.engine_type : drawerEngineType;

    let filteredAssetList = assetList || [];
    if (activeAssetType) {
      filteredAssetList = filteredAssetList.filter(item => item.asset_type === activeAssetType);
      if (activeAssetType === 1 && activeAircraftType) {
        filteredAssetList = filteredAssetList.filter(item => item.aircraft_type_id === activeAircraftType);
      }
      if (activeAssetType === 2 && activeEngineType) {
        filteredAssetList = filteredAssetList.filter(item => item.engine_type_id === activeEngineType);
      }
    }

    return {
      asset_type: { title: 'Asset Type', inputType: 'dropdown', options: ASSET_TYPE_OPTIONS, valueKey: 'value', labelKey: 'label', multiple: false, dependentFields: ['aircraft_type', 'engine_type', 'asset_id'] },
      ...(hasAircraft && activeAssetType === 1 ? { aircraft_type: { title: 'Aircraft Type', inputType: 'dropdown', options: aircraftTypes, valueKey: 'id', labelKey: 'name', multiple: false, dependentFields: ['asset_id'] } } : {}),
      ...(hasEngine && activeAssetType === 2 ? { engine_type: { title: 'Engine Type', inputType: 'dropdown', options: engineTypes, valueKey: 'id', labelKey: 'name', multiple: false, dependentFields: ['asset_id'] } } : {}),
      asset_id: { title: 'Asset', inputType: 'dropdown', options: filteredAssetList, valueKey: 'unique_key', labelKey: 'name', multiple: true, loading: assetListLoading, renderOption: (props, option) => ( <Box component="li" {...props} key={option.slug || option.unique_key}> {option.name} </Box>) },
      status: { title: 'Status', inputType: 'dropdown', options: assetStatusOptions, valueKey: 'value', labelKey: 'label', multiple: false },
      ownership_type: { title: 'Ownership', inputType: 'dropdown', options: ownerShipTypes || [], valueKey: 'value', labelKey: 'label', multiple: true },
      lessee_id: { title: 'Lessee', inputType: 'dropdown', options: lessee || [], valueKey: 'id', labelKey: 'name', multiple: false },
      lessor_name_id: { title: 'Lessor/Holdco', inputType: 'dropdown', options: owner || [], valueKey: 'id', labelKey: 'name', multiple: false },
      owner_id: { title: 'Owner/SPV', inputType: 'dropdown', options: owner || [], valueKey: 'id', labelKey: 'name', multiple: false },
      onDependencyChange: (keyParam, value) => {
        if (keyParam === 'asset_type') {
          setDrawerAssetType(value != null ? value : null);
          setDrawerAircraftType(null);
          setDrawerEngineType(null);
        }
        if (keyParam === 'aircraft_type') setDrawerAircraftType(value != null ? value : null);
        if (keyParam === 'engine_type') setDrawerEngineType(value != null ? value : null);
      },
      onOpen: () => { setDrawerAssetType(undefined); setDrawerAircraftType(undefined); setDrawerEngineType(undefined); },
    };
  }, [assetList, assetListLoading, aircraftTypes, engineTypes, lessee, owner, assetType, hasAircraft, hasEngine, filter.asset_type, filter.aircraft_type, filter.engine_type, drawerAssetType, drawerAircraftType, drawerEngineType]);

  useEffect(() => {
    getAssetsList('skeletonLoader', { per_page: 50 });
    trackActivity('Page Viewed', { page_title: 'Assets' });
    if (!lessee?.length) dispatch(getLesseeApi());
    if (!lessor?.length) dispatch(getOwnerLessorApi());
    if (!owner?.length) dispatch(getOwnerListApi());
    globalGetService(getAircraftTypeList()).then(r => { if (checkApiStatus(r)) setAircraftTypes(r?.data?.data); });
    globalGetService(getEngineTypeList()).then(r => { if (checkApiStatus(r)) setEngineTypes(r?.data?.data?.engineTypes); });
    setAssetListLoading(true);
    globalGetService(getAssetListApi({}, true)).then(r => {
      setAssetListLoading(false);
      if (checkApiStatus(r)) {
        const formatted = (r?.data?.data || []).map(item => ({ ...item, unique_key: `${item.id}_${item.asset_type}` }));
        setAssetList(formatted);
      }
    });
  }, []);

  const getAssetsList = (loaderType, query = {}) => {
    const queryParams = Object.assign({}, query);
    if (queryParams.asset_id) {
      queryParams.asset_id = queryParams.asset_id.toString().split(',').map(key => { const [id, type] = key.split('_'); return `(${id},${type})`; });
    }
    loaderType === 'skeletonLoader' ? setSkeletonLoader(true) : setLoading(true);
    globalGetService(getAssetsApi(params, queryParams))
      .then(response => {
        loaderType === 'skeletonLoader' ? setSkeletonLoader(false) : setLoading(false);
        const responseData = response?.data?.data;
        setiAssetsInfo(responseData);
        if (!query?.sort && !query?.sort_by) setSortInfo({ sort: '', sort_by: '' });
      });
  };

  const onApplyFilter = (query) => {
    const cleanQuery = Object.fromEntries(Object.entries(query).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    // The Asset Type field inside the filter drawer can set asset_type independently of the tab
    // click handler (onChangeTab) — keep `assetType` in sync so tab counts/highlighting and the
    // drawer stay consistent regardless of which control the user used.
    const impliedType = drawerAssetType !== undefined ? (drawerAssetType || null) : (cleanQuery.asset_type || null);
    if ((impliedType || 0) !== assetType) setAssetType(impliedType || 0);
    setFilter(cleanQuery);
    getAssetsList('', { ...cleanQuery, ...(impliedType ? { asset_type: impliedType } : {}), page: 1, per_page: assetsInfo?.pagination?.per_page || 50 });
  };

  const onChangeTab = (event, newValue) => {
    setAssetType(newValue);
    setFilter({});
    getAssetsList('pageLoader', { ...(newValue ? { asset_type: newValue } : {}), per_page: 50 });
  };
    
  const createSortHandler = (key) => {
    let sortQuery = { sort_by: key };
    if (sortInfo.sort_by === key) {
      sortQuery = { ...sortQuery, sort: sortInfo.sort === 'asc' ? 'desc' : 'asc' }
    } else {
      sortQuery = { ...sortQuery, sort: 'asc' }
    }
    setSortInfo(sortQuery)
    getAssetsList('', {
      ...sortQuery,
      ...filter,
      ...(assetType ? { asset_type: assetType } : {}),
      page: assetsInfo?.pagination?.current_page || 1,
      per_page: assetsInfo?.pagination?.per_page || 50,
    });
  }

  const handleExport = () => {
    let query = { ...filter, export: true, ...(assetType ? { asset_type: assetType } : {}) }
    if (sortInfo?.sort && sortInfo?.sort_by) {
      query = {
        ...query,
        sort: sortInfo?.sort,
        sort_by: sortInfo?.sort_by
      }
    }
    if (query.asset_id) {
      query.asset_id = query.asset_id.toString().split(',').map(key => { const [id, type] = key.split('_'); return `(${id},${type})`; });
    }
    seIsExport(true);
    globalGetService(getAssetsApi(params, query)).then((response) => {
      if (checkApiStatus(response)) {
        enqueueSnackbar(
          response?.data.message,
          { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        seIsExport(false);
      }
    })
  }
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Assets`, page_type: page_type, })
  }

  return (
    <section className="active-asset-section" style={{margin: "-8px -3px 0 -5px"}}>
      <DeploymentBannerWithOffset>
        {(bannerHeight) => (
      <div className="active-asset-header">
        {
          skeletonLoader ? <STableLoader count={6} rowCount={14} /> : <>
            {hasAnyAssetCount ? <AssetTabs assetCount={assetCount} onChangeTab={onChangeTab} filter={{ asset_type: assetType }} activeCount={activeTabCount} /> : null}
            <Paper elevation={0} className="lease-module-paper" sx={{ padding: '5px', marginBottom: '2px' }}>
              <Grid container spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Grid size='grow' sx={{ minWidth: 0 }}>
                  <FilterComponent filter={filter} filterMenu={AssetFilterMenu} getResponseBack={onApplyFilter} />
                </Grid>
                <Grid size={{ md: 'auto', xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <ul className='list-inline' style={{ textAlign: 'right', float: 'none' }}>
                    {assetsInfo?.list?.length > 0 ? <li className='lease-mobile-button'><Button startIcon={isExport ? <ButtonLoader size={15} /> : <ArrowDownwardIcon />} onClick={() => { handleExport(); handleItemClicked('Export Button'); }} variant="outlined" size='small' className='btn-primary action-btn-start-icon'>Export</Button></li> : null}
                    <li className='lease-mobile-button'><AddAssetBtn /></li>
                  </ul>
                </Grid>
              </Grid>
            </Paper>
            <Paper elevation={0} className="active-asset-list lease-module-paper">
              <div style={{ ...(assetsInfo?.list?.length > 0 ? { height: `${window.innerHeight - 240 - bannerHeight}px`, maxHeight: 'none' } : { height: 'auto', maxHeight: `${window.innerHeight - 240 - bannerHeight}px` }), overflow: "auto" }}>
                <Table className="mui-table-format" stickyHeader>
                  <AssetsHd sortInfo={sortInfo} createSortHandler={createSortHandler} activeTab={assetType} />
                  <TableBody>
                    {assetsInfo?.list?.length > 0 && assetsInfo?.list?.map((item, index) => {
                      return (
                        <AssetsList
                          onView={() => { navigate(`/assets/${item?.asset_type}/${assetTypeMap[item?.asset_type]}/${item?.slug}/leases`, { state: { asset: { id: item?.id, name: item?.asset_name, asset_type: item?.asset_type, asset_slug: item?.slug } } }); }}
                          key={index}
                          item={item}
                          activeTab={assetType}
                          onClicktechnicalSpecification={() => window.open(`${domain}technical-specification/${assetTypeMap[item?.asset_type] === "landing_gear" ? "lg" : assetTypeMap[item?.asset_type]}/${item?.slug}/basic-details`, "_blank")}
                          onVariableRent={() => setVariableRent({ modal: true, data: item })}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
                {!skeletonLoader && assetsInfo?.list?.length === 0 ? <NoRecordFound /> : null}
              </div>
              <Paper>
                <Pagination
                  pagination={assetsInfo?.pagination}
                  onPageChange={(event, newPage) => getAssetsList('', { ...filter, ...(assetType ? { asset_type: assetType } : {}), ...(sortInfo.sort_by ? { sort: sortInfo.sort, sort_by: sortInfo.sort_by } : {}), page: newPage + 1, per_page: assetsInfo?.pagination?.per_page })}
                  onRowsPerPageChange={(e) => getAssetsList('', { ...filter, ...(assetType ? { asset_type: assetType } : {}), ...(sortInfo.sort_by ? { sort: sortInfo.sort, sort_by: sortInfo.sort_by } : {}), page: 1, per_page: e.target.value })} />
              </Paper>
            </Paper>
          </>
        }
      </div>
        )}
      </DeploymentBannerWithOffset>
      {variableRent?.modal && <VariableRentModal
      variableRent={variableRent}
        onToggleModal={() => setVariableRent({ modal: false, data: null })}
      />
      }
      {isLoading ? <PageLoader /> : null}
    </section >
  );
};
export default Assets;
